// Notifications push, vues de l’application : abonnement de ce navigateur, confié au serveur qui le
// retrouvera au moment d’un nouveau rendez-vous. La clé VITE_VAPID_PUBLIC_KEY est publique par nature
// (comparable à une adresse) ; seule la clé privée, côté serveur, permet de signer les envois.
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

export type PushStatus = 'unsupported' | 'denied' | 'off' | 'on';

let tokenProvider: () => Promise<string | null> = async () => null;

/** Fournit le jeton de session Supabase aux appels serveur. */
export function setPushTokenProvider(fn: () => Promise<string | null>): void {
  tokenProvider = fn;
}

function urlBase64ToUint8Array(base64url: string) {
  const base64 = (base64url + '='.repeat((4 - (base64url.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

async function call(path: string, body: unknown): Promise<void> {
  const token = await tokenProvider();
  const res = await fetch(path, { method: 'POST', headers: { 'content-type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify(body) });
  if (!res.ok) throw new Error(`Le serveur n’a pas pu enregistrer l’abonnement (HTTP ${res.status}).`);
}

export function pushSupported(): boolean {
  return !!VAPID_PUBLIC_KEY && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export async function pushStatus(): Promise<PushStatus> {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  return sub ? 'on' : 'off';
}

/** Demande l’autorisation, crée l’abonnement de ce navigateur et le confie au serveur. */
export async function pushSubscribe(): Promise<void> {
  if (!pushSupported() || !VAPID_PUBLIC_KEY) throw new Error('Les notifications ne sont pas prises en charge sur cet appareil.');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error(permission === 'denied' ? 'Autorisation refusée. Réactivez les notifications dans les réglages du navigateur.' : 'Autorisation non accordée.');
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) });
  const data = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  try {
    await call('/api/push/subscribe', { endpoint: data.endpoint, keys: data.keys });
  } catch (e) {
    await sub.unsubscribe().catch(() => { /* sans importance */ });
    throw e;
  }
}

/** Retire l’abonnement de ce navigateur, localement et côté serveur. */
export async function pushUnsubscribe(): Promise<void> {
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  await call('/api/push/unsubscribe', { endpoint }).catch(() => { /* l’abonnement local est retiré, l’oubli serveur peut attendre */ });
}
